import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Modal } from "antd";
import { Context, Hooks, Util } from "@xt/xtbase";
import cardInnerBg from "@/assets/images/activity/voucher/card-inner-bg.svg";
import modalStyles from "@/components/Pages/SpikexCard/spikexModal.module.less";
import styles from "./index.module.less";
import {
  getBindMobileCouponOffer,
  isUserMobileBound,
  markBindPhonePromptShown,
  shouldDeferBindPhoneForHomeCouponPrompt,
  shouldShowBindPhonePrompt,
  shouldSkipBindPhonePromptInApp,
  shouldSkipBindPhonePromptPath,
} from "@/utils/bindPhonePrompt";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

const PROMPT_DELAY_MS = 1500;

function isMockBindPhonePromptPreview(query) {
  const v = query?.mockBindPhonePrompt;
  if (v == null) return false;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

export default function BindPhonePrompt() {
  const router = useRouter();
  const { t } = useTranslation();
  const [appState] = useContext(XtContext);
  const { loginStatus, userInfo } = appState;
  const userId = userInfo?.userId;

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const isMockPreview = useMemo(
    () => isMockBindPhonePromptPreview(router.query),
    [router.query?.mockBindPhonePrompt]
  );

  const couponOffer = useMemo(() => {
    const fromUser = getBindMobileCouponOffer(userInfo);
    if (fromUser) return fromUser;
    if (isMockPreview) return { amount: "200", name: "" };
    return null;
  }, [isMockPreview, userInfo]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return undefined;

    let cancelled = false;
    let timerId = null;

    const openPrompt = () => {
      if (cancelled) return;
      markBindPhonePromptShown(userId);
      setVisible(true);
    };

    const scheduleOpen = () => {
      timerId = window.setTimeout(openPrompt, PROMPT_DELAY_MS);
    };

    const run = () => {
      if (isMockPreview) {
        if (loginStatus === 1) {
          setVisible(true);
        }
        return;
      }

      if (loginStatus !== 1 || !userId || !userInfo) return;
      if (shouldSkipBindPhonePromptInApp()) return;
      if (userInfo.userAccountLevel === 2) return;
      if (isUserMobileBound(userInfo)) return;
      if (shouldSkipBindPhonePromptPath(router.pathname)) return;
      if (shouldDeferBindPhoneForHomeCouponPrompt(userId, userInfo, router.pathname)) return;
      if (!shouldShowBindPhonePrompt(userId)) return;

      scheduleOpen();
    };

    run();

    return () => {
      cancelled = true;
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [
    mounted,
    isMockPreview,
    loginStatus,
    userId,
    userInfo,
    router.pathname,
  ]);

  const closePrompt = useCallback(() => {
    setVisible(false);
  }, []);

  const handleDismiss = useCallback(() => {
    if (!isMockPreview && userId) {
      markBindPhonePromptShown(userId);
    }
    closePrompt();
  }, [closePrompt, isMockPreview, userId]);

  const handleLink = useCallback(() => {
    if (!isMockPreview && userId) {
      markBindPhonePromptShown(userId);
    }
    closePrompt();
    window.location.href = getUrl("/accounts/user/mobile");
  }, [closePrompt, isMockPreview, userId]);

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable
      onCancel={handleDismiss}
      centered
      width={360}
      destroyOnClose
      className={modalStyles.modalRoot}
      wrapClassName={modalStyles.modalWrap}
      zIndex={10050}
    >
      <div className={modalStyles.modalBody}>
        <div className={modalStyles.modalTitle}>{t("account.bindPhonePromptTitle")}</div>
        <div className={modalStyles.modalDesc}>{t("account.bindPhonePromptBody")}</div>
        {couponOffer ? (
          <div className={styles.coupon}>
            <img className={styles.couponBg} src={cardInnerBg} alt="" />
            <div className={styles.couponInner}>
              <div className={styles.couponShade} />
            </div>
            <div className={styles.badge}>{t("account.bindPhonePromptCouponBadge")}</div>
            <div className={styles.separator} />
            <div className={styles.content}>
              <div className={styles.amount}>
                {couponOffer.amount}
                <span className={styles.unit}> USDT</span>
              </div>
              <div className={styles.name}>
                {couponOffer.name || t("account.bindPhonePromptCouponName")}
              </div>
            </div>
            <div className={styles.hint}>{t("account.bindPhonePromptCouponHint")}</div>
          </div>
        ) : null}
        <div className={`${modalStyles.modalActions} ${modalStyles.modalActionsSingle}`}>
          <button type="button" className={modalStyles.btnPrimary} onClick={handleLink}>
            {t("account.bindPhonePromptLink")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
